/*
  HQ Cart extras — the cart page's free-shipping bar and care-product upsell.

  Both live inside main-cart-items' .js-contents, the block Dawn's cart.js
  re-renders on every quantity change, so their markup is always rebuilt by
  Liquid from the current cart. This file only does the two things Liquid
  cannot:

  1. The free-shipping bar when the cart is NOT in the store's currency.
     Liquid has no exchange rate, so for a shopper paying in hryvnia or euros
     the section prints the threshold in store cents, the cart total in the
     shopper's cents and a sample of the store's money format, hidden. Here
     the threshold is converted with Shopify's own rate, rounded UP to a whole
     unit (erring high never tells a shopper shipping is free before checkout
     agrees), and the amount is formatted by the pattern Liquid produced —
     never with a hardcoded symbol. With no rate the bar simply stays hidden:
     no bar is better than a wrong one. A cart in the store's currency is
     rendered entirely by Liquid and never reaches this code.

  2. The upsell's one-tap "Add": /cart/add.js is asked for exactly the
     sections cart.js itself refreshes, and they are swapped the same way, so
     the lines, the bar, the total and the count bubble move together.

  Everything is delegated or re-run from a MutationObserver on <cart-items>:
  cart.js replaces this markup wholesale, and anything bound to the old nodes
  would die with them.
*/
(function () {
  if (window.hqCartExtrasReady) return;
  window.hqCartExtrasReady = true;

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* ---------- Money format, learned from Liquid ------------------------- */

  /* `sample` is Liquid's rendering of 1234567 cents in the shopper's
     currency — "$12,345.67", "₴12,345.67", "12.345,67 €", "¥12,346". Its
     prefix, suffix, separators and whether it shows cents are read off it. */
  function moneyFormatter(sample) {
    var first = sample.search(/\d/);
    if (first < 0) return null;
    var last = first;
    for (var i = sample.length - 1; i >= first; i--) {
      if (/\d/.test(sample.charAt(i))) { last = i; break; }
    }
    var prefix = sample.slice(0, first);
    var suffix = sample.slice(last + 1);
    var core = sample.slice(first, last + 1);
    var hasCents = core.replace(/\D/g, '').length === 7;
    var decimalSep = hasCents ? core.charAt(core.length - 3) : '';
    var integerPart = hasCents ? core.slice(0, core.length - 3) : core;
    var groupMatch = integerPart.match(/\D/);
    var groupSep = groupMatch ? groupMatch[0] : '';

    /* dropZeroCents: "₴54,232" rather than "₴54,232.00" — for a round
       threshold printed as a label, the same as Liquid's
       money_without_trailing_zeros on the store-currency side. */
    return function (cents, dropZeroCents) {
      var units = hasCents ? Math.floor(cents / 100) : Math.round(cents / 100);
      var integer = String(units).replace(/\B(?=(\d{3})+(?!\d))/g, groupSep);
      var showCents = hasCents && !(dropZeroCents && cents % 100 === 0);
      var text = showCents ? integer + decimalSep + String(cents % 100).padStart(2, '0') : integer;
      return prefix + text + suffix;
    };
  }

  /* ---------- Free-shipping bar in another currency --------------------- */

  function renderBar(bar) {
    if (bar.dataset.hqFsDone) return;
    bar.dataset.hqFsDone = '1';

    var rate = parseFloat(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate);
    var threshold = parseInt(bar.dataset.threshold, 10);
    var total = parseInt(bar.dataset.total, 10);
    var format = moneyFormatter(bar.dataset.moneySample || '');
    if (!rate || !threshold || isNaN(total) || !format) return;

    var localThreshold = Math.ceil((threshold * rate) / 100) * 100;
    var left = Math.max(0, localThreshold - total);
    var percent = Math.min(100, Math.floor((total * 100) / localThreshold));

    var message = bar.querySelector('.hq-fs__msg');
    var progress = bar.querySelector('.hq-fs__bar');
    if (message) {
      message.textContent = '';
      if (left === 0) {
        bar.classList.add('is-reached');
        message.textContent = bar.dataset.reached || '';
      } else {
        /* Built from text nodes, not innerHTML: the wording comes from the
           theme editor and must not be able to inject markup. */
        var parts = (bar.dataset.remaining || '').split('[amount]');
        message.appendChild(document.createTextNode(parts[0] || ''));
        var amount = document.createElement('strong');
        amount.textContent = format(left);
        message.appendChild(amount);
        message.appendChild(document.createTextNode(parts.slice(1).join('[amount]')));
      }
    }
    if (progress) {
      progress.value = percent;
      progress.textContent = percent + '%';
    }
    var badgeText = bar.querySelector('.hq-fs__badge-text');
    if (badgeText) {
      badgeText.textContent = (bar.dataset.badge || '').split('[threshold]').join(format(localThreshold, true));
    }
    bar.hidden = false;
  }

  function renderBars() {
    document.querySelectorAll('[data-hq-fs]').forEach(renderBar);
  }

  /* ---------- Upsell: one-tap add --------------------------------------- */

  function showUpsellError(text) {
    var box = document.querySelector('[data-hq-upsell-error]');
    if (!box) return;
    box.textContent = text;
    box.hidden = false;
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-hq-upsell-add]');
    if (!button || button.getAttribute('aria-busy') === 'true') return;

    var cartItems = document.querySelector('cart-items');
    var variantId = parseInt(button.getAttribute('data-hq-upsell-add'), 10);
    if (!cartItems || typeof cartItems.getSectionsToRender !== 'function' || !variantId) return;

    event.preventDefault();
    button.setAttribute('aria-busy', 'true');

    var sections = cartItems.getSectionsToRender();

    fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        sections: sections.map(function (section) { return section.section; }),
        sections_url: window.location.pathname
      })
    })
      .then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) throw new Error(data.description || data.message || 'This item could not be added.');
          return data;
        });
      })
      .then(function (data) {
        sections.forEach(function (section) {
          var html = data.sections && data.sections[section.section];
          var target = document.getElementById(section.id);
          if (!html || !target) return;
          var element = target.querySelector(section.selector) || target;
          element.innerHTML = cartItems.getSectionInnerHTML(html, section.selector);
        });
      })
      .catch(function (error) {
        button.removeAttribute('aria-busy');
        showUpsellError(error.message);
      });
  });

  /* ---------- Upsell row: arrows and mouse drag ------------------------ */

  /* One card plus the gap after it: what an arrow click moves, and the grid
     a drag settles onto. */
  function upsellStep(list) {
    var item = list.querySelector('.hq-upsell__item');
    if (!item) return list.clientWidth;
    var style = getComputedStyle(list);
    var gap = parseFloat(style.columnGap || style.gap) || 0;
    return item.getBoundingClientRect().width + gap;
  }

  /* Arrows appear only when the row overflows (a fifth product on desktop),
     and each one is disabled at its end. Only attributes and classes change
     here, so the MutationObserver below (childList only) never re-triggers. */
  function updateUpsell(box) {
    var list = box.querySelector('[data-hq-upsell-list]');
    if (!list) return;
    var scrollable = list.scrollWidth > list.clientWidth + 2;
    box.classList.toggle('is-scrollable', scrollable);
    var prev = box.querySelector('[data-hq-upsell-prev]');
    var next = box.querySelector('[data-hq-upsell-next]');
    if (prev) {
      prev.hidden = !scrollable;
      prev.disabled = list.scrollLeft <= 2;
    }
    if (next) {
      next.hidden = !scrollable;
      next.disabled = list.scrollLeft + list.clientWidth >= list.scrollWidth - 2;
    }
  }

  function updateUpsells() {
    document.querySelectorAll('[data-hq-upsell]').forEach(updateUpsell);
  }

  document.addEventListener('click', function (event) {
    var arrow = event.target.closest('[data-hq-upsell-prev], [data-hq-upsell-next]');
    if (!arrow) return;
    var box = arrow.closest('[data-hq-upsell]');
    var list = box && box.querySelector('[data-hq-upsell-list]');
    if (!list) return;
    var direction = arrow.hasAttribute('data-hq-upsell-next') ? 1 : -1;
    list.scrollBy({ left: direction * upsellStep(list), behavior: 'smooth' });
  });

  /* scroll does not bubble; listening in the capture phase catches the list's
     own scrolling without binding to a node cart.js may replace. */
  var upsellFrame = 0;
  document.addEventListener('scroll', function (event) {
    if (!event.target.closest || !event.target.closest('[data-hq-upsell-list]')) return;
    cancelAnimationFrame(upsellFrame);
    upsellFrame = requestAnimationFrame(updateUpsells);
  }, true);

  /* Mouse drag. Touch keeps the browser's own swipe: pointerType is checked
     so a finger never lands in this code. The drag only starts past a few
     pixels, so an ordinary click on a card or its Add button is untouched;
     after a real drag the click that follows is swallowed, or letting go over
     "Add" would put the product in the cart. */
  var drag = null;
  var swallowClick = false;

  document.addEventListener('pointerdown', function (event) {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    var list = event.target.closest('[data-hq-upsell-list]');
    if (!list || list.scrollWidth <= list.clientWidth + 2) return;
    drag = { list: list, x: event.clientX, left: list.scrollLeft, moved: false, id: event.pointerId };
  });

  document.addEventListener('pointermove', function (event) {
    if (!drag || event.pointerId !== drag.id) return;
    var dx = event.clientX - drag.x;
    if (!drag.moved) {
      if (Math.abs(dx) < 6) return;
      drag.moved = true;
      drag.list.classList.add('is-dragging');
    }
    drag.list.scrollLeft = drag.left - dx;
  });

  function endDrag() {
    if (!drag) return;
    var list = drag.list;
    if (drag.moved) {
      swallowClick = true;
      setTimeout(function () { swallowClick = false; }, 0);
      var step = upsellStep(list);
      var target = Math.round(list.scrollLeft / step) * step;
      list.classList.remove('is-dragging');
      list.scrollTo({ left: target, behavior: 'smooth' });
    }
    drag = null;
  }
  document.addEventListener('pointerup', endDrag);
  document.addEventListener('pointercancel', endDrag);

  document.addEventListener('click', function (event) {
    if (!swallowClick) return;
    if (!event.target.closest('[data-hq-upsell-list]')) return;
    event.preventDefault();
    event.stopPropagation();
    swallowClick = false;
  }, true);

  /* A photo dragged by the mouse would start the browser's image drag. */
  document.addEventListener('dragstart', function (event) {
    if (event.target.closest && event.target.closest('[data-hq-upsell-list]')) event.preventDefault();
  });

  /* ---------- Start, and follow every re-render ------------------------- */

  function refresh() {
    renderBars();
    updateUpsells();
  }

  function start() {
    refresh();
    window.addEventListener('load', updateUpsells);
    window.addEventListener('resize', updateUpsells);
    var cartItems = document.querySelector('cart-items');
    if (!cartItems || !window.MutationObserver) return;
    /* Bars that are already drawn carry data-hq-fs-done, and the upsell
       update only touches attributes, so the observer re-entering on this
       file's own writes is a no-op, not a loop. */
    new MutationObserver(refresh).observe(cartItems, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
